package com.swetdot.swetgram.ui.moderation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.moderation.AdminRepository
import com.swetdot.swetgram.data.moderation.ModerationReport
import com.swetdot.swetgram.data.moderation.ModerationText
import com.swetdot.swetgram.data.moderation.ReportAction
import com.swetdot.swetgram.data.moderation.ReportDecision
import com.swetdot.swetgram.data.moderation.ReportStatus
import com.swetdot.swetgram.data.moderation.ReportTargetKind
import com.swetdot.swetgram.domain.AccountRole
import com.swetdot.swetgram.domain.Page
import com.swetdot.swetgram.ui.profile.profileMessage
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.text
import kotlinx.coroutines.launch
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.moderation_action_desc_block_user
import swetgram.shared.generated.resources.moderation_action_desc_delete_message
import swetgram.shared.generated.resources.moderation_action_desc_delete_permanent
import swetgram.shared.generated.resources.moderation_action_desc_delete_post

/*
 * THE ADMIN PANEL, AND WHY IT IS IN THE PHONE APP AT ALL
 *
 * STA §16.1 has a view and it is NOT this one. It says the v1 admin panel "can be built as: simple
 * protected admin web page; or internal API + minimal frontend; or temporary tool using
 * server-rendered Go templates". All three are server-side web; none is a mobile client. This
 * change disagrees, and the disagreement rests on the CONTRACT rather than on preference -- which
 * is the order the brief for this change requires, and which matters because where the folder and
 * the contract disagree the contract wins:
 *
 *  1. **`X-Client-Platform` is REQUIRED on all eight admin paths and its enum is exactly
 *     `ios | android`.** Frozen v1.0.0 declares the header `required: true` and lists two values;
 *     there is no `web`. A browser-based admin page cannot send a legal value for a header the
 *     contract makes mandatory -- it would have to claim to be an iOS or an Android app. STA §16.1
 *     was written before the contract froze; the contract is the source of truth and it does not
 *     contemplate a web caller. This is the single strongest fact here.
 *  2. **There is no web tier in this repository, and building one is not a small thing.** All three
 *     of STA §16.1's options presuppose one, and the third names Go templates -- this backend is
 *     Ktor and Kotlin, so that option is already stale. A web admin means a second delivery
 *     channel with its own session handling, its own refresh-token dance and its own CSRF story,
 *     none of which exists and none of which is in `:shared`.
 *  3. **STA §16.1's actual constraint is about SCOPE, not medium**: "Do not build a complex admin
 *     product in v1." A queue, a report, and a decision is not a complex product.
 *
 * WHAT THE DISAGREEMENT COSTS, stated rather than hidden:
 *  * The panel ships in every user's APK. That is not a security hole -- the server enforces
 *    `admin_role_required` against `users.role` and the client's gate decides only what to DRAW --
 *    but anyone who unpacks the app learns a panel exists. They would learn the same from
 *    `openapi.yaml`, which is in this repository and describes all eight operations.
 *  * Moderating on a phone is worse than moderating on a desktop, and this panel is strictly less
 *    capable than STA §16.1's, because v1.0.0 declares no operation that can render the reported
 *    CONTENT. See [ModerationReportUiState].
 *
 * IT IS INVISIBLE, NOT DISABLED. `SettingsScreen` composes the entry only when `getMe` has
 * ACTUALLY ANSWERED and said `moderator` or `admin` -- see [canOpenModeration]. A greyed-out
 * "Moderation" row would tell every user in the world that a panel exists and that they are not
 * allowed in, which is the failure mode `GroupScreen` already argues against for its own manage
 * rows ("a greyed row invites a tap that can only ever refuse").
 */

// ---------------------------------------------------------------------------------------------
// The source
// ---------------------------------------------------------------------------------------------

/**
 * The three admin operations the panel uses, and no more.
 *
 * AN INTERFACE, for the reason `DiscoverPostDetailSource` is one: it makes these view models
 * constructible in a test on a host with no network. [asModerationSource] is the one-line adapter,
 * so wiring needs no edit to `AdminRepository`.
 *
 * THREE OF EIGHT. `adminBlockUser` and the three deletes are reached through [resolve]'s `action`,
 * which performs the side effect and decides the report in one server-side transaction;
 * `adminUnblockUser` has no listing operation to reach it from. `AdminRepository`'s header argues
 * both.
 */
interface ModerationSource {

    /** `adminListReports`. Newest first, keyset-paginated. */
    suspend fun reports(
        status: ReportStatus?,
        targetKind: ReportTargetKind?,
        cursor: String?,
    ): Page<ModerationReport>

    /** `adminGetReport`. No side effects -- it does not claim the report into `reviewing`. */
    suspend fun report(reportId: String): ModerationReport

    /** `adminResolveReport`. Terminal, and the side effect runs before the status write. */
    suspend fun resolve(
        reportId: String,
        decision: ReportDecision,
        action: ReportAction,
        note: String?,
    ): ModerationReport
}

fun AdminRepository.asModerationSource(): ModerationSource = object : ModerationSource {
    override suspend fun reports(
        status: ReportStatus?,
        targetKind: ReportTargetKind?,
        cursor: String?,
    ) = this@asModerationSource.reports(status, targetKind, cursor)

    override suspend fun report(reportId: String) = this@asModerationSource.report(reportId)

    override suspend fun resolve(
        reportId: String,
        decision: ReportDecision,
        action: ReportAction,
        note: String?,
    ) = this@asModerationSource.resolve(reportId, decision, action, note)
}

// ---------------------------------------------------------------------------------------------
// The gate
// ---------------------------------------------------------------------------------------------

/**
 * Whether this account may see the moderation entry at all.
 *
 * MODERATOR AND ADMIN ARE ONE AUDIENCE, which is SPS §16.4 read literally rather than a preference:
 * it grants "admin/moderator" a single list that includes suspending users, and STA §16.1
 * distinguishes no roles either. The server's `AdminPermission` reaches the same answer from the
 * same two sentences, so the client's gate and the server's rule agree by construction rather than
 * by coincidence.
 *
 * NULL IS FALSE, and it is the case that matters most: null means `getMe` has not answered yet, and
 * drawing a moderation entry optimistically -- then removing it a beat later for an ordinary
 * account -- is how every user learns the panel exists.
 *
 * [AccountRole.UNKNOWN] IS ALSO FALSE. It means the server sent a role this build has never heard
 * of, and a client that granted itself a panel on a role it cannot reason about is guessing. The
 * cost is that a future fourth role would have to wait for an app update to see the entry; the
 * server would refuse it anyway until this build knew what it was.
 *
 * THIS IS NOT A SECURITY BOUNDARY and must never be mistaken for one. It decides what to draw. The
 * server decides what may happen, and answers `403 admin_role_required` to everybody else.
 */
fun canOpenModeration(role: AccountRole?): Boolean =
    role == AccountRole.MODERATOR || role == AccountRole.ADMIN

// ---------------------------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------------------------

data class ModerationQueueUiState(
    val reports: List<ModerationReport> = emptyList(),
    /** Null shows every status. The contract's filter is optional and so is this. */
    val filter: ReportStatus? = ReportStatus.OPEN,
    val loading: Boolean = false,
    val loadingMore: Boolean = false,
    val hasMore: Boolean = false,
    val loadedOnce: Boolean = false,
    val error: String? = null,
    /**
     * True when the server refused the whole surface.
     *
     * Separate from [error] because it is not a failure to retry: `403 admin_role_required` means
     * this account is not staff, and a Try again button over it would be a button that exists to
     * fail. It is reachable in one real case -- a role that was revoked while the app was open.
     */
    val forbidden: Boolean = false,
)

/**
 * `adminListReports`, newest first.
 *
 * DEFAULTS TO `open`, which is the only default that makes the screen a QUEUE rather than an
 * archive. Everything else is one tap away.
 *
 * NO CACHE AND NO OPTIMISM anywhere in this class. A moderation queue rendered from a stale local
 * copy shows reports a colleague has already decided, and acting on one of those is how the same
 * account gets suspended twice for one thing.
 */
class ModerationQueueViewModel(
    private val source: ModerationSource,
) : ViewModel() {

    private val _state = MutableStateFlow(ModerationQueueUiState())
    val state: StateFlow<ModerationQueueUiState> = _state.asStateFlow()

    /** Opaque, server-owned, and reset on every refresh -- a cursor into a list that has changed. */
    private var cursor: String? = null

    init {
        refresh()
    }

    fun refresh() {
        if (_state.value.loading) return
        val filter = _state.value.filter
        _state.value = _state.value.copy(loading = true, error = null)
        viewModelScope.launch {
            try {
                val page = source.reports(status = filter, targetKind = null, cursor = null)
                cursor = page.nextCursor
                _state.value = ModerationQueueUiState(
                    reports = page.items,
                    filter = filter,
                    hasMore = page.hasMore,
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loading = false,
                    loadedOnce = true,
                    forbidden = error is ApiError.Forbidden,
                    error = error.moderationBanner("Could not load the report queue."),
                )
            }
        }
    }

    /** Re-reads from the top with a new filter, because a cursor is scoped to the old query. */
    fun setFilter(filter: ReportStatus?) {
        if (_state.value.filter == filter) return
        cursor = null
        _state.value = _state.value.copy(filter = filter, reports = emptyList(), loadedOnce = false)
        refresh()
    }

    fun loadMore() {
        val current = _state.value
        // `hasMore` and a cursor are two different claims; requiring both stops a request that
        // could only repeat the first page.
        if (current.loading || current.loadingMore || !current.hasMore) return
        val next = cursor ?: return

        _state.value = current.copy(loadingMore = true)
        viewModelScope.launch {
            try {
                val page = source.reports(
                    status = current.filter,
                    targetKind = null,
                    cursor = next,
                )
                cursor = page.nextCursor
                _state.value = _state.value.copy(
                    reports = _state.value.reports + page.items,
                    hasMore = page.hasMore,
                    loadingMore = false,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // `hasMore` is left alone, so the end of the queue can be retried.
                _state.value = _state.value.copy(
                    loadingMore = false,
                    error = error.moderationBanner("Could not load more reports."),
                )
            }
        }
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}

// ---------------------------------------------------------------------------------------------
// One report
// ---------------------------------------------------------------------------------------------

data class ModerationReportUiState(
    val report: ModerationReport? = null,
    val loadedOnce: Boolean = false,
    /** A resolve is in flight. */
    val busy: Boolean = false,
    val error: String? = null,
    val forbidden: Boolean = false,
    /** The report is not there. Terminal: nothing on the screen has anything to act on. */
    val missing: Boolean = false,
    /**
     * Flips once when a decision lands, so the screen can pop back to the queue. A ONE-SHOT
     * signal -- the caller navigates and then calls [ModerationReportViewModel.consumeDecided], or
     * the next recomposition pops again.
     */
    val decided: Boolean = false,
) {
    /**
     * What the resolve form may offer, decided once against the report rather than in the view.
     *
     * See [availableActions] for the argument. Empty until the report has loaded, because every
     * one of these is a question about what kind of thing was reported.
     */
    val actions: List<ReportAction> get() = report?.let { availableActions(it) }.orEmpty()

    /** A decided report is read-only: no v1.0.0 operation reopens one. */
    val isDecided: Boolean get() = report?.status?.isOpen == false
}

/**
 * `adminGetReport` and `adminResolveReport`.
 *
 * IT RE-READS RATHER THAN TAKING THE QUEUE'S ROW. The list is a snapshot, and re-reading before the
 * decision form is drawn is what shows a moderator that a colleague already decided this one. It
 * cannot PREVENT the race -- only the server can, and it does: an already-decided report answers
 * 200 with the decision that stands and performs nothing -- but it means the screen tells the truth
 * before anything is pressed.
 */
class ModerationReportViewModel(
    private val reportId: String,
    private val source: ModerationSource,
) : ViewModel() {

    private val _state = MutableStateFlow(ModerationReportUiState())
    val state: StateFlow<ModerationReportUiState> = _state.asStateFlow()

    private var loading = false

    init {
        refresh()
    }

    fun refresh() {
        if (loading) return
        loading = true
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    report = source.report(reportId),
                    loadedOnce = true,
                    error = null,
                    missing = false,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    forbidden = error is ApiError.Forbidden,
                    missing = error is ApiError.NotFound,
                    error = error.moderationBanner("Could not load that report."),
                )
            } finally {
                loading = false
            }
        }
    }

    /**
     * Decides it, with an optional side effect.
     *
     * The server runs [action] BEFORE the status write and in the same transaction, so a delete
     * that fails leaves the report undecided rather than decided-and-not-acted-on. It also answers
     * 200 for an already-decided report WITHOUT performing anything -- so the returned row, not the
     * request, is what the screen then shows.
     *
     * [note] is stored in `reports.resolution_note` and **cannot be read back by any v1.0.0
     * operation**: `Report` has no property for it. The screen says so where it is collected.
     */
    fun resolve(decision: ReportDecision, action: ReportAction, note: String?) {
        if (_state.value.busy) return
        _state.value = _state.value.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                val decided = source.resolve(
                    reportId = reportId,
                    decision = decision,
                    action = action,
                    // Blank becomes null: an empty note is not a note.
                    note = note?.trim()?.takeIf { it.isNotEmpty() }?.take(ModerationText.NOTE_MAX),
                )
                _state.value = _state.value.copy(report = decided, busy = false, decided = true)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // The report stays on screen. A failed resolve must never look like a decision.
                _state.value = _state.value.copy(
                    busy = false,
                    error = error.moderationBanner("Could not resolve that report."),
                )
            }
        }
    }

    /** Consumed by the caller BEFORE it pops; see [ModerationReportUiState.decided]. */
    fun consumeDecided() {
        _state.value = _state.value.copy(decided = false)
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}

// ---------------------------------------------------------------------------------------------
// Rules and copy -- pure, so they can be exercised without a renderer
// ---------------------------------------------------------------------------------------------

/*
 * TODO(localization): the strings below move into resources for EN + RU, along with the rest of the
 * moderation surface. They are not urgent -- this panel is staff-only -- but they are not exempt.
 *
 * BLOCKED ON WHERE THE KEYS GO, and worth naming so this does not read as merely undone. The four
 * action descriptions in [actionDescription] above are already resourced; the rest of this panel's
 * copy belongs beside them under the `moderation_` prefix in `values/strings.xml`, which is the
 * shared file rather than a per-area one. See `ReportFlow.kt`'s note for the full argument and for
 * the genuinely hard part, which is `targetNoun` appearing mid-sentence in an oblique case.
 */

/**
 * Which side effects the resolve form offers for a given report.
 *
 * [ReportAction.NONE] IS ALWAYS THERE. "This was a violation and I have dealt with it another way"
 * and "this was not a violation" both need a decision with no side effect.
 *
 * [ReportAction.DELETE_CONTENT] ONLY WHERE THERE IS CONTENT -- a message, a Discover post or a
 * Discover comment. The rule is the server's own, read off `content_hash`, which is null for a
 * `user` or a `group` target precisely because neither has a single piece of content to hash.
 * Offering it on an account report would be asking the server to delete something nobody named.
 *
 * [ReportAction.BLOCK_USER] ONLY FOR AN ACCOUNT REPORT, and this one is a DELIBERATE
 * UNDER-OFFER worth stating plainly. The contract declares `action` as a flat three-value enum with
 * no per-target rule, so `block_user` on a report about a POST is syntactically legal -- and
 * nothing in v1.0.0 says WHOSE account it would suspend. Presumably the author's; the server's own
 * notes do not say, and it has never been probed live because nothing is routed yet. Suspending the
 * wrong person is not a mistake that can be walked back from a phone: `adminUnblockUser` exists but
 * no operation LISTS suspensions, so finding the account again means knowing its uuid. So the
 * client offers it only where the report literally names the account, and a moderator who wants to
 * suspend the author of a post has to open a report about that author. Recorded as a known gap; the
 * pass that routes these owes the question a live probe.
 */
fun availableActions(report: ModerationReport): List<ReportAction> = buildList {
    add(ReportAction.NONE)
    if (report.namesContent) add(ReportAction.DELETE_CONTENT)
    if (report.targetKind == ReportTargetKind.PERSON) add(ReportAction.BLOCK_USER)
}

fun actionLabel(action: ReportAction, kind: ReportTargetKind?): String = when (action) {
    ReportAction.NONE -> "Do nothing else"
    ReportAction.DELETE_CONTENT -> "Delete the ${kind?.let { targetNoun(it) } ?: "content"}"
    ReportAction.BLOCK_USER -> "Suspend the account"
}

/**
 * What each action actually does, shown UNDER its option rather than in a confirmation the
 * moderator meets after choosing.
 *
 * Every sentence here is a fact about the server, not a paraphrase:
 *  * a Discover post is a HARD delete and it cascades to every comment on it, including other
 *    people's;
 *  * a Discover comment is a hard delete that also decrements the post's count;
 *  * a message is a soft delete for everyone, with a tombstone, bypassing both the author check
 *    and the 15-minute window;
 *  * a suspension revokes every live session -- and, today, does not stop a new sign-in, which is a
 *    server gap this panel must not pretend away.
 */
fun actionDescription(action: ReportAction, kind: ReportTargetKind?): UiText? = when (action) {
    ReportAction.NONE -> null
    ReportAction.DELETE_CONTENT -> when (kind) {
        ReportTargetKind.DISCOVER_POST ->
            Res.string.moderation_action_desc_delete_post.text()
        ReportTargetKind.DISCOVER_COMMENT -> Res.string.moderation_action_desc_delete_permanent.text()
        ReportTargetKind.MESSAGE ->
            Res.string.moderation_action_desc_delete_message.text()
        else -> Res.string.moderation_action_desc_delete_permanent.text()
    }
    // WAS FALSE UNTIL THE SUSPENSION WAS ACTUALLY ENFORCED, and it is worth saying why the wrong
    // sentence was dangerous rather than merely wrong. It read "it can still sign back in", which
    // told a moderator that suspension was decorative -- and a moderator who believes that reaches
    // for something harsher than the case needs. `AuthService.login` and the JWT validate block
    // both consult `user_suspensions` now (DECISIONS.md §21), so the account is refused at the
    // door and refused on the token it already holds.
    ReportAction.BLOCK_USER -> Res.string.moderation_action_desc_block_user.text()
}

fun decisionLabel(decision: ReportDecision): String = when (decision) {
    ReportDecision.RESOLVE -> "This breaks the rules"
    ReportDecision.REJECT -> "This is fine"
}

/** Both are one-way doors: no operation in v1.0.0 reopens a decided report. */
fun decisionDescription(decision: ReportDecision): String = when (decision) {
    ReportDecision.RESOLVE -> "Closes the report as a violation."
    ReportDecision.REJECT -> "Closes the report as not a violation."
}

fun reportStatusLabel(status: ReportStatus): String = when (status) {
    ReportStatus.OPEN -> "Open"
    ReportStatus.REVIEWING -> "Being reviewed"
    ReportStatus.RESOLVED -> "Resolved"
    ReportStatus.REJECTED -> "Rejected"
    // Not "Error". A status this build does not know is a newer server, and the moderator should
    // see that rather than a row that looks broken.
    ReportStatus.UNKNOWN -> "Unrecognised status"
}

/**
 * What was reported, in a word.
 *
 * A `target_type` this build does not know is PRINTED RAW rather than hidden, because a queue that
 * silently drops rows it does not understand is a queue with an invisible backlog. The row is drawn
 * and cannot be acted on, which is the honest pair.
 */
fun reportTargetLabel(report: ModerationReport): String = when (report.targetKind) {
    ReportTargetKind.PERSON -> "Account"
    ReportTargetKind.MESSAGE -> "Message"
    ReportTargetKind.DISCOVER_POST -> "Post"
    ReportTargetKind.DISCOVER_COMMENT -> "Comment"
    ReportTargetKind.GROUP -> "Group"
    ReportTargetKind.PICSO_POST -> "Picso"
    null -> report.targetType
}

/** Same rule as [reportTargetLabel]: an unknown reason is shown as the server spelled it. */
fun reportReasonLabel(report: ModerationReport): String =
    report.knownReason?.let { reasonLabel(it) } ?: report.reason

/**
 * The banner text for a failure on this surface.
 *
 * `403` is the one worth its own sentence, because it is not a fault: it means this account is not
 * staff, or is not staff any more. Everything with no moderation meaning falls through to
 * `profileMessage`, so offline, a timeout, an ended session and an unsupported build are spelled
 * the same way here as everywhere else in the app -- the second copy of that table is exactly what
 * `ProfileViewModel.withSaveFailure` says it refuses to make.
 */
internal fun Throwable.moderationBanner(fallback: String): String = when (this) {
    is ApiError.Forbidden -> "This account does not have moderator access."
    is ApiError.NotFound -> "That report is no longer there."
    else -> profileMessage(fallback)
}
